import { useMediaQuery } from "@mui/material";
import {
  BlockWrapper,
  defineBlock,
  smartProps,
  useInspectorControl,
  useEditor,
} from "@smartfire/js-block-engine";
import { EditableText } from "@smartfire/components";
import he from "he";
import { lazy } from "react";
import { Socials } from "../components";
import SiteSelector from "./SiteSelector";
import { ReactComponent as Logo } from "../assets/full_white_logo.svg";
import { ReactComponent as DecoL } from "./assets/decoL.svg";
import { ReactComponent as DecoR } from "./assets/decoR.svg";
import { ReactComponent as DecoMobileL } from "./assets/decoMobileL.svg";
import { ReactComponent as DecoMobileR } from "./assets/decoMobileR.svg";
import style from "./style.module.scss";

const Inspector = lazy(() => import("./inspector.js"));

function Footer({
  topMenu,
  bottomMenu,
  socials,
  firstLink,
  secondLink,
  copyright,
  currentSite,
  otherSites,
  logo,
}) {
  useInspectorControl(Inspector);
  const { isEditing } = useEditor();
  const isMobile = useMediaQuery(`(max-width: ${style.mobileBreakpoint})`);
  const isFirstLinkDefined =
    (firstLink.linkType === "other" && firstLink.url) ||
    (firstLink.linkType === "page" && firstLink.pageId);
  const isSecondLinkDefined =
    (secondLink.linkType === "other" && secondLink.url) ||
    (secondLink.linkType === "page" && secondLink.pageId);

  return (
    <BlockWrapper.Footer className={style.block}>
      <div className={style.topFooter}>
        {!isMobile ? (
          <>
            <DecoL className={style.decoL} />
            <DecoR className={style.decoR} />
          </>
        ) : (
          <>
            <DecoMobileL className={style.decoL} />
            <DecoMobileR className={style.decoR} />
          </>
        )}

        <div className={style.container}>
          <div className={style.logoContainer}>
            {logo?.url ? (
              <img
                src={logo.url}
                alt="Seezon Logo"
                className={style.logo}
                loading="lazy"
              />
            ) : (
              <Logo className={style.logo} aria-label="Seezon Logo" />
            )}
          </div>

          <div className={style.divider} />

          <div className={style.menu}>
            {topMenu &&
              Object.values(topMenu).map((item) => (
                <div key={item.ID} className={style.item}>
                  <a href={item.url}>{he.decode(item.title)}</a>

                  {item.children &&
                    Object.values(item.children).map((child) => (
                      <a key={child.ID} href={child.url}>
                        {he.decode(child.title)}
                      </a>
                    ))}
                </div>
              ))}

            <div className={style.otherLinks}>
              {((isEditing && isFirstLinkDefined) || firstLink.url) && (
                <a href={firstLink.url}>{firstLink.label}</a>
              )}

              {((isEditing && isSecondLinkDefined) || secondLink.url) && (
                <a href={secondLink.url}>{secondLink.label}</a>
              )}

              {!isMobile && (
                <Socials socials={socials} className={style.socials} />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={style.bottomFooter}>
        <div className={style.container}>
          <div className={style.menu}>
            {!isMobile && (isEditing || copyright) && (
              <EditableText
                tagName="p"
                smartProp="copyright"
                placeholder="Copyright..."
              />
            )}

            {bottomMenu &&
              Object.values(bottomMenu).map((item) => (
                <a key={item.ID} href={item.url}>
                  {he.decode(item.title)}
                </a>
              ))}

            {isMobile && copyright && (
              <EditableText
                tagName="p"
                smartProp="copyright"
                placeholder="Copyright..."
              />
            )}
          </div>

          <SiteSelector currentSite={currentSite} otherSites={otherSites} />
        </div>
        {isMobile && <span style={{ height: "85px", display: "block" }} />}
      </div>
    </BlockWrapper.Footer>
  );
}

export default defineBlock({
  Block: Footer,
  setup: {
    title: "Footer",
  },
  smartProps: {
    logo: smartProps.image,
    firstLink: smartProps.object.withDefault({
      linkType: "other",
      url: "",
      pageId: 0,
      label: "Find your shop",
    }),
    secondLink: smartProps.object.withDefault({
      linkType: "other",
      url: "",
      pageId: 0,
      label: "Contact",
    }),
    copyright: smartProps.string.withDefault(""),
  },
});
